import React, { useEffect, useState } from 'react';
import {
  Typography, Card, Button, Input, InputNumber, Space, Descriptions, Tag, Alert, Spin, message, Modal, Segmented, Switch,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, SafetyOutlined, DollarOutlined, SwapOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AiSupportConfig, serverControlApi } from '../services/api';

const { Title, Text } = Typography;

const DEFAULT_AI_CONFIG: AiSupportConfig = {
  enabled: false,
  provider: 'openai',
  model: 'gpt-5.4',
  base_url: '',
  request_timeout: 30,
  low_risk_auto_reply_enabled: true,
  allowed_auto_reply_intents: [],
  force_handoff_keywords: [],
  api_key: '',
};

const Settings: React.FC = () => {
  const [newToken, setNewToken] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [thresholdValue, setThresholdValue] = useState<number | null>(null);
  const [aiConfig, setAiConfig] = useState<AiSupportConfig>(DEFAULT_AI_CONFIG);
  const [weightDraft, setWeightDraft] = useState<Record<string, number>>({});
  const queryClient = useQueryClient();

  const { data: botInfo, isLoading, refetch } = useQuery({
    queryKey: ['bot-info'],
    queryFn: serverControlApi.getBotInfo,
  });

  const { data: thresholdData, isLoading: thresholdLoading } = useQuery({
    queryKey: ['withdrawal-review-threshold'],
    queryFn: serverControlApi.getWithdrawalReviewThreshold,
  } as any);

  const { data: recommendedData, isLoading: recommendedLoading } = useQuery({
    queryKey: ['recommended-payment'],
    queryFn: serverControlApi.getRecommendedPayment,
  });

  const { data: aiConfigData, isLoading: aiConfigLoading } = useQuery({
    queryKey: ['ai-support-config'],
    queryFn: serverControlApi.getAiSupportConfig,
  } as any);

  const { data: weightsData, isLoading: weightsLoading } = useQuery({
    queryKey: ['payment-weights'],
    queryFn: serverControlApi.getPaymentWeights,
  } as any);

  useEffect(() => {
    const groups = (weightsData as any)?.groups || [];
    if (!groups.length) return;
    const next: Record<string, number> = {};
    groups.forEach((g: any) => {
      (g.members || []).forEach((mm: any) => { next[mm.id] = mm.weight; });
    });
    setWeightDraft(next);
  }, [weightsData]);

  const weightsMutation = useMutation({
    mutationFn: (w: Record<string, number>) => serverControlApi.setPaymentWeights(w),
    onSuccess: () => {
      message.success('支付通道权重已保存');
      queryClient.invalidateQueries({ queryKey: ['payment-weights'] });
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.detail || '保存失败');
    },
  });

  useEffect(() => {
    if (thresholdValue === null && thresholdData) {
      setThresholdValue((thresholdData as any)?.threshold ?? 0);
    }
  }, [thresholdData, thresholdValue]);

  useEffect(() => {
    if (!aiConfigData) return;
    setAiConfig({
      ...DEFAULT_AI_CONFIG,
      ...(aiConfigData as any),
      api_key: '',
    });
  }, [aiConfigData]);

  const recommendedMutation = useMutation({
    mutationFn: (methodId: string) => serverControlApi.setRecommendedPayment(methodId),
    onSuccess: () => {
      message.success('推荐支付方式已切换');
      queryClient.invalidateQueries({ queryKey: ['recommended-payment'] });
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.detail || '切换失败');
    },
  });

  const currentRecommended = (recommendedData as any)?.method_id || 'alipay_2';

  const thresholdMutation = useMutation({
    mutationFn: (val: number) => serverControlApi.setWithdrawalReviewThreshold(val),
    onSuccess: () => {
      message.success('提现审核阈值已保存');
      queryClient.invalidateQueries({ queryKey: ['withdrawal-review-threshold'] });
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.detail || '保存失败');
    },
  });

  const currentThreshold = (thresholdData as any)?.threshold ?? 0;
  const displayThreshold = thresholdValue !== null ? thresholdValue : currentThreshold;
  const hasApiKey = aiConfig.api_key_configured || !!aiConfig.api_key;

  const testMutation = useMutation({
    mutationFn: (token: string) => serverControlApi.testBotToken(token),
    onSuccess: (data: any) => {
      setTestResult(data);
      if (data.valid) {
        message.success(`Token有效: @${data.username}`);
      } else {
        message.error(`Token无效: ${data.error}`);
      }
    },
    onError: () => message.error('验证请求失败'),
  });

  const updateMutation = useMutation({
    mutationFn: (token: string) => serverControlApi.updateBotToken(token),
    onSuccess: (data: any) => {
      message.success(data.message || 'Token已更新，服务重启中...');
      setNewToken('');
      setTestResult(null);
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.detail || '更新失败');
    },
  });

  const aiConfigMutation = useMutation({
    mutationFn: (data: AiSupportConfig) => serverControlApi.updateAiSupportConfig(data),
    onSuccess: (data: any) => {
      message.success('AI 客服配置已保存');
      queryClient.invalidateQueries({ queryKey: ['ai-support-config'] });
      setAiConfig((prev) => ({ ...prev, ...data, api_key: '' }));
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.detail || 'AI 配置保存失败');
    },
  });

  const aiTestMutation = useMutation({
    mutationFn: (data: Partial<AiSupportConfig>) => serverControlApi.testAiProvider(data),
    onSuccess: () => {
      message.success('OpenAI 连接测试成功');
      queryClient.invalidateQueries({ queryKey: ['ai-support-config'] });
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.detail || '连接测试失败');
    },
  });

  const handleTest = () => {
    const val = newToken.trim();
    if (!val) {
      message.warning('请输入Token');
      return;
    }
    setTestResult(null);
    testMutation.mutate(val);
  };

  const handleUpdate = () => {
    if (!testResult?.valid) {
      message.warning('请先验证Token');
      return;
    }
    Modal.confirm({
      title: '确认更换主机器人',
      content: (
        <div>
          <p>确定将主机器人更换为 <strong>@{testResult.username}</strong>？</p>
          <p style={{ color: '#ff4d4f' }}>更换后服务将自动重启，期间所有功能短暂中断。</p>
        </div>
      ),
      okText: '确认更换',
      cancelText: '取消',
      okType: 'danger',
      onOk: () => updateMutation.mutate(newToken.trim()),
    });
  };

  const info = botInfo as any;

  return (
    <div className="space-y-6">
      <Title level={2}>系统设置</Title>

      {/* 当前主机器人信息 */}
      <Card
        title="主机器人信息"
        size="small"
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading} size="small">
            刷新
          </Button>
        }
      >
        {isLoading ? (
          <div className="text-center py-8"><Spin /></div>
        ) : info ? (
          <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
            <Descriptions.Item label="状态">
              <Tag
                color={info.status === 'online' ? 'success' : 'error'}
                icon={info.status === 'online' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              >
                {info.status === 'online' ? '在线' : '离线'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="用户名">
              {info.username ? `@${info.username}` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Bot ID">
              {info.bot_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="名称">
              {info.first_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Token" span={2}>
              <Text code>{info.token_masked}</Text>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Text type="secondary">无法获取机器人信息</Text>
        )}
      </Card>

      {/* 更换 Token */}
      <Card title="更换主机器人Token" size="small">
        <Alert
          message="更换Token后服务将自动重启，期间管理后台和机器人功能会短暂中断（约10秒）"
          type="warning"
          showIcon
          className="mb-4"
        />

        <Space direction="vertical" className="w-full" size="middle">
          <div>
            <Text className="block mb-2">新Token（从 @BotFather 获取）</Text>
            <Space.Compact style={{ width: '100%' }}>
              <Input.Password
                value={newToken}
                onChange={(e) => {
                  setNewToken(e.target.value);
                  setTestResult(null);
                }}
                placeholder="粘贴新机器人的Token"
                style={{ flex: 1 }}
              />
              <Button
                onClick={handleTest}
                loading={testMutation.isPending}
              >
                验证
              </Button>
            </Space.Compact>
          </div>

          {testResult && (
            <Alert
              type={testResult.valid ? 'success' : 'error'}
              message={
                testResult.valid
                  ? `Token有效 — @${testResult.username} (${testResult.first_name}, ID: ${testResult.bot_id})`
                  : `Token无效 — ${testResult.error}`
              }
              showIcon
            />
          )}

          <Button
            type="primary"
            icon={<SafetyOutlined />}
            onClick={handleUpdate}
            disabled={!testResult?.valid}
            loading={updateMutation.isPending}
          >
            保存并重启
          </Button>
        </Space>
      </Card>

      <Card title="AI 客服配置" size="small" loading={aiConfigLoading}>
        <Space direction="vertical" className="w-full" size="middle">
          <Alert
            message="OpenAI 连接配置"
            description="这里只配置 GPT 接口本身；知识库内容和 AI 预览在“客服消息”页面维护。"
            type="info"
            showIcon
          />

          <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
            <Descriptions.Item label="启用 AI 客服">
              <Switch
                checked={aiConfig.enabled}
                onChange={(checked) => setAiConfig((prev) => ({ ...prev, enabled: checked }))}
              />
            </Descriptions.Item>
            <Descriptions.Item label="API Key 状态">
              {hasApiKey ? <Tag color="success">已配置</Tag> : <Tag color="default">未配置</Tag>}
              {aiConfig.api_key_masked && (
                <Text code style={{ marginLeft: 8 }}>{aiConfig.api_key_masked}</Text>
              )}
            </Descriptions.Item>
          </Descriptions>

          <div>
            <Text className="block mb-2">OpenAI API Key</Text>
            <Input.Password
              value={aiConfig.api_key}
              onChange={(e) => setAiConfig((prev) => ({ ...prev, api_key: e.target.value }))}
              placeholder={aiConfig.api_key_configured ? '留空表示保留当前 Key' : '粘贴 OpenAI API Key'}
            />
            <Text type="secondary">出于安全考虑，刷新页面后不会回显明文 Key；以“已配置”和右侧掩码为准。</Text>
          </div>

          <div>
            <Text className="block mb-2">模型</Text>
            <Input
              value={aiConfig.model}
              onChange={(e) => setAiConfig((prev) => ({ ...prev, model: e.target.value }))}
              placeholder="gpt-5.4"
            />
          </div>

          <div>
            <Text className="block mb-2">Base URL（可选）</Text>
            <Input
              value={aiConfig.base_url || ''}
              onChange={(e) => setAiConfig((prev) => ({ ...prev, base_url: e.target.value }))}
              placeholder="https://zz.naoy.me 或 https://api.openai.com/v1"
            />
            <Text type="secondary">支持填写根地址、带 /v1 的地址，或完整 /responses 地址；系统不会强制追加 /v1。</Text>
          </div>

          <div>
            <Text className="block mb-2">请求超时（秒）</Text>
            <InputNumber
              min={5}
              max={120}
              value={aiConfig.request_timeout}
              onChange={(val) => setAiConfig((prev) => ({ ...prev, request_timeout: Number(val || 30) }))}
            />
          </div>

          <Space>
            <Button
              onClick={() => aiTestMutation.mutate(aiConfig)}
              loading={aiTestMutation.isPending}
            >
              测试连接
            </Button>
            <Button
              type="primary"
              icon={<SafetyOutlined />}
              onClick={() => aiConfigMutation.mutate(aiConfig)}
              loading={aiConfigMutation.isPending}
            >
              保存 AI 配置
            </Button>
          </Space>
        </Space>
      </Card>

      {/* 支付通道分配（2026-10-20） */}
      <Card title="支付通道分配" size="small" loading={weightsLoading} extra={<SwapOutlined />}>
        <Space direction="vertical" className="w-full" size="middle">
          <Text type="secondary">
            按权重把订单分配给组内各通道，填 0 表示不参与。保存时自动归一化为百分比，即时生效。
          </Text>
          {((weightsData as any)?.groups || [])
            .filter((g: any) => g.id === 'alipay' || g.id === 'wechat')
            .map((g: any) => {
              const groupTotal = (g.members || []).reduce(
                (s: number, mm: any) => s + Number(weightDraft[mm.id] ?? 0), 0);
              return (
                <div key={g.id}>
                  <Text strong>{g.icon} {g.name}</Text>
                  {(g.members || []).map((mm: any) => (
                    <div key={mm.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                      <span>
                        {mm.name}
                        {!mm.enabled && <Text type="secondary">（未启用）</Text>}
                        {mm.max_amount
                          ? <Text type="secondary">　区间 ¥{mm.min_amount}–{mm.max_amount}</Text>
                          : null}
                      </span>
                      <Space>
                        <InputNumber
                          min={0} max={100} style={{ width: 90 }}
                          disabled={!mm.enabled}
                          value={weightDraft[mm.id] ?? 0}
                          onChange={(v) => setWeightDraft((prev) => ({ ...prev, [mm.id]: Number(v ?? 0) }))}
                        />
                        <Text type="secondary" style={{ width: 56, display: 'inline-block', textAlign: 'right' }}>
                          {groupTotal > 0
                            ? ((Number(weightDraft[mm.id] ?? 0) / groupTotal) * 100).toFixed(1)
                            : '0.0'}%
                        </Text>
                      </Space>
                    </div>
                  ))}
                </div>
              );
            })}
          <Button type="primary" loading={weightsMutation.isPending}
            onClick={() => weightsMutation.mutate(weightDraft)}>💾 保存</Button>
        </Space>
      </Card>

      {/* 提现审核设置 */}
      <Card title="提现审核设置" size="small" loading={thresholdLoading}>
        <Space direction="vertical" className="w-full" size="middle">
          <div>
            <Text className="block mb-2">审核阈值（USDT）</Text>
            <Space.Compact>
              <InputNumber
                value={displayThreshold}
                onChange={(val) => setThresholdValue(val ?? 0)}
                min={0}
                step={10}
                precision={2}
                style={{ width: 200 }}
                prefix={<DollarOutlined />}
                addonAfter="USDT"
              />
              <Button
                type="primary"
                onClick={() => thresholdMutation.mutate(displayThreshold)}
                loading={thresholdMutation.isPending}
              >
                保存
              </Button>
            </Space.Compact>
          </div>
          <Alert
            type="info"
            showIcon
            message={
              currentThreshold > 0
                ? `当前已启用：提现到账金额 >= ${currentThreshold} USDT 时需要管理员审核`
                : '当前未启用：所有提现自动处理，无需审核'
            }
            description="设为 0 表示关闭审核，所有提现自动执行。设置阈值后，大额提现将进入待审核状态，管理员可在 Telegram 或资金管理页面进行审批。"
          />
        </Space>
      </Card>
    </div>
  );
};

export default Settings;
